import React, { useState, useEffect } from 'react';
import CollapsibleInfo from '../components/CollapsibleInfo';
import { appStorage, auth } from '../../../firebase.config';
import { useEnhancedCompany } from '../../../contexts/EnhancedCompanyContext';

const BankDetailsStep = ({ data, errors, onChange, onTouch }) => {
  const { currentCompany } = useEnhancedCompany();
  const [showPassword, setShowPassword] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Load base64 data from Firestore on component mount
  useEffect(() => {
    const loadBase64Data = async () => {
      try {
        if (!currentCompany?.companyId && !currentCompany?.id) return;
        
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('../../../firebase.config');
        
        const companyId = currentCompany?.companyId || currentCompany?.id;
        const docRef = doc(db, 'companies', companyId);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const companyData = docSnap.data();
          
          // Load UPI QR base64 if available
          if (companyData.upiBase64 && !data.upiQrUrl) {
            console.log('📥 Loading UPI QR from Firestore base64 data');
            handleChange('upiQrUrl', companyData.upiBase64);
          }
        }
      } catch (error) {
        console.warn('⚠️ Could not load base64 data from Firestore:', error);
      }
    };

    loadBase64Data();
  }, [currentCompany, data.upiQrUrl]);

  const handleChange = (field, value) => {
    onChange(field, value);
    onTouch(field);
  };

  const handleAccountNumberChange = (value) => {
    console.log('🔄 handleAccountNumberChange called with:', value);
    // Remove any non-numeric characters
    const cleanValue = value.replace(/\D/g, '');
    console.log('🔄 Clean value:', cleanValue);
    handleChange('bankAccount', cleanValue);
  };

  const handleAccountConfirmChange = (value) => {
    // Remove any non-numeric characters
    const cleanValue = value.replace(/\D/g, '');
    handleChange('bankAccountConfirm', cleanValue);
  };

  const handleIfscChange = (value) => {
    // Convert to uppercase and limit to 11 characters
    const cleanValue = value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 11);
    handleChange('bankIfsc', cleanValue);
  };

  // Helper function for base64 fallback
  const handleBase64Fallback = async (file) => {
    try {
      console.log('🔄 Converting to base64...');
      const base64Data = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.readAsDataURL(file);
      });
      
      console.log('✅ Base64 conversion successful');
      
      // Save base64 to Firebase Firestore for cross-device access
      try {
        const companyId = currentCompany?.companyId || currentCompany?.id || auth.currentUser?.uid;
        await saveBase64ToFirestore(base64Data, companyId, 'upi', file.name);
        console.log('✅ Base64 saved to Firestore for cross-device access');
      } catch (firestoreError) {
        console.warn('⚠️ Could not save to Firestore, using local storage only:', firestoreError);
      }
      
      // Update form data with base64
      handleChange('upiQrUrl', base64Data);
      
      setUploadProgress(100);
      
      // Reset progress after delay
      setTimeout(() => {
        setUploadProgress(0);
        setUploading(false);
      }, 1000);
      
      alert('Upload completed using offline mode. File will be available on all devices.');
      return true;
      
    } catch (fallbackError) {
      console.error('❌ Base64 fallback failed:', fallbackError);
      return false;
    }
  };

  // Helper function to save base64 to Firestore
  const saveBase64ToFirestore = async (base64Data, companyId, category, fileName) => {
    try {
      const { doc, setDoc, serverTimestamp } = await import('firebase/firestore');
      const { db } = await import('../../../firebase.config');
      
      const docRef = doc(db, 'companies', companyId);
      const data = {
        [`${category}Base64`]: base64Data,
        [`${category}FileName`]: fileName,
        [`${category}UploadDate`]: serverTimestamp(),
        lastUpdated: serverTimestamp()
      };
      
      await setDoc(docRef, data, { merge: true });
      console.log('✅ Base64 data saved to Firestore');
    } catch (error) {
      console.error('❌ Failed to save base64 to Firestore:', error);
      throw error;
    }
  };

  const handleFileUpload = async (file) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file (PNG, JPG, JPEG)');
      return;
    }

    // Validate file size (1MB max)
    if (file.size > 1 * 1024 * 1024) {
      alert('File size should be less than 1MB');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    // Add timeout mechanism to force fallback after 30 seconds
    const timeoutId = setTimeout(() => {
      console.log('⏰ Upload timeout reached, forcing base64 fallback...');
      handleBase64Fallback(file);
    }, 30000);

    try {
      console.log('📤 Uploading UPI QR to Firebase Storage:', { 
        fileName: file.name, 
        size: file.size, 
        fileType: file.type,
        user: auth.currentUser?.uid,
        companyId: currentCompany?.companyId || currentCompany?.id
      });
      
      // Check if user is authenticated
      if (!auth.currentUser) {
        throw new Error('User not authenticated. Please login again.');
      }

      const companyId = currentCompany?.companyId || currentCompany?.id || auth.currentUser?.uid;
      
      // Upload to Firebase Storage
      setUploadProgress(50);
      const firebaseURL = await appStorage.uploadCompanyAsset(file, companyId, 'upi');
      
      // Clear timeout since upload succeeded
      clearTimeout(timeoutId);
      
      // Update progress
      setUploadProgress(100);
      
      // Update form data
      handleChange('upiQrUrl', firebaseURL);
      
      console.log('✅ Firebase upload successful:', firebaseURL);
      
      // Reset progress after delay
      setTimeout(() => {
        setUploadProgress(0);
        setUploading(false);
      }, 1000);

    } catch (error) {
      // Clear timeout since we're handling the error
      clearTimeout(timeoutId);
      
      console.error('❌ Firebase upload failed:', error);
      console.error('❌ Error details:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      
      // Try base64 fallback for any error
      console.log('🔄 Upload failed, trying base64 fallback...');
      const fallbackSuccess = await handleBase64Fallback(file);
      
      if (!fallbackSuccess) {
        alert(`Upload failed: ${error.message}. Please try again.`);
        setUploading(false);
        setUploadProgress(0);
      }
    }
  };

  return (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold text-gray-900 mb-1">Banking Details</h3>
        <p className="text-gray-600">Add your business bank account for payments and transactions</p>
      </div>

      <div className="space-y-4">
        {/* Bank Name */}
        <div>
          <label htmlFor="bankName" className="block text-sm font-medium text-gray-700 mb-1">
            Bank Name *
          </label>
          <input
            id="bankName"
            name="bankName"
            type="text"
            value={data.bankName || ''}
            onChange={(e) => handleChange('bankName', e.target.value)}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.bankName ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Enter bank name (e.g., State Bank of India)"
          />
          {errors.bankName && (
            <p className="mt-1 text-sm text-red-600">{errors.bankName}</p>
          )}
        </div>

        {/* Account Number */}
        <div>
          <label htmlFor="bankAccount" className="block text-sm font-medium text-gray-700 mb-1">
            Account Number *
          </label>
          <input
            id="bankAccount"
            name="bankAccount"
            type="text"
            value={data.bankAccount || ''}
            onChange={(e) => handleAccountNumberChange(e.target.value)}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.bankAccount ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Enter account number"
            maxLength={20}
          />
          {errors.bankAccount && (
            <p className="mt-1 text-sm text-red-600">{errors.bankAccount}</p>
          )}
        </div>

        {/* Confirm Account Number */}
        <div>
          <label htmlFor="bankAccountConfirm" className="block text-sm font-medium text-gray-700 mb-1">
            Re-enter Account Number *
          </label>
          <div className="relative">
            <input
              id="bankAccountConfirm"
              name="bankAccountConfirm"
              type={showPassword ? "text" : "password"}
              value={data.bankAccountConfirm || ''}
              onChange={(e) => handleAccountConfirmChange(e.target.value)}
              className={`w-full px-4 py-3 pr-12 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.bankAccountConfirm ? 'border-red-500' : 
                data.bankAccount && data.bankAccountConfirm && data.bankAccount === data.bankAccountConfirm 
                  ? 'border-green-500' : 'border-gray-300'
              }`}
              placeholder="Re-enter account number to confirm"
              maxLength={20}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showPassword ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L8.464 8.464M9.878 9.878l-6.415-6.414M14.121 14.121L22.536 22.536" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              )}
            </button>
          </div>
          {errors.bankAccountConfirm && (
            <p className="mt-1 text-sm text-red-600">{errors.bankAccountConfirm}</p>
          )}
          {data.bankAccount && data.bankAccountConfirm && data.bankAccount === data.bankAccountConfirm && (
            <p className="mt-1 text-sm text-green-600 flex items-center">
              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              Account numbers match
            </p>
          )}
        </div>

        {/* IFSC Code */}
        <div>
          <label htmlFor="bankIfsc" className="block text-sm font-medium text-gray-700 mb-1">
            IFSC Code *
          </label>
          <input
            id="bankIfsc"
            name="bankIfsc"
            type="text"
            value={data.bankIfsc || ''}
            onChange={(e) => handleIfscChange(e.target.value)}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.bankIfsc ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Enter IFSC code (e.g., SBIN0001234)"
            maxLength={11}
          />
          {errors.bankIfsc && (
            <p className="mt-1 text-sm text-red-600">{errors.bankIfsc}</p>
          )}
          <p className="mt-1 text-sm text-gray-500">
            11-character bank code (4 letters + 7 characters)
          </p>
        </div>

        {/* UPI ID */}
        <div>
          <label htmlFor="upiId" className="block text-sm font-medium text-gray-700 mb-1">
            UPI ID (Optional)
          </label>
          <input
            id="upiId"
            name="upiId"
            type="text"
            value={data.upiId || ''}
            onChange={(e) => handleChange('upiId', e.target.value)}
            className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300"
            placeholder="e.g., mybusiness@upi"
          />
          <p className="mt-1 text-sm text-gray-500">Your UPI VPA for receiving payments</p>
        </div>

        {/* UPI QR Code (Optional) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            UPI QR Code (Optional)
          </label>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-gray-400 transition-colors">
            <div className="text-center">
              {uploading ? (
                <div className="space-y-4">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Uploading QR Code...</p>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{uploadProgress}%</p>
                  </div>
                </div>
              ) : data.upiQrUrl ? (
                <div className="space-y-4">
                  <div className="relative inline-block">
                    <img
                      src={data.upiQrUrl}
                      alt="UPI QR Code"
                      className="w-24 h-24 object-contain mx-auto rounded-lg border border-gray-200"
                    />
                    <button
                      onClick={() => handleChange('upiQrUrl', '')}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-green-600">✓ QR Code uploaded</p>
                    <button
                      onClick={() => document.getElementById('qr-upload').click()}
                      className="mt-2 text-sm text-blue-600 hover:text-blue-700"
                    >
                      Change QR Code
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                    <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <div className="mt-4">
                    <label htmlFor="qr-upload" className="cursor-pointer">
                      <span className="mt-2 block text-sm font-medium text-gray-900">
                        Upload UPI QR Code
                      </span>
                      <span className="mt-1 block text-sm text-gray-500">
                        PNG, JPG up to 1MB
                      </span>
                    </label>
                  </div>
                </>
              )}
              
              <input
                id="qr-upload"
                name="qr-upload"
                type="file"
                className="sr-only"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    handleFileUpload(file);
                  }
                }}
                disabled={uploading}
                aria-label="Upload UPI QR Code"
              />
            </div>
          </div>
        </div>
      </div>


      {/* Collapsible Info Box */}
      <CollapsibleInfo title="Banking Security Guidelines">
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• <strong>Account Number:</strong> Enter your primary business bank account</li>
          <li>• <strong>IFSC Code:</strong> 11-character code for digital payments (Check your bank statement)</li>
          <li>• <strong>UPI QR Code:</strong> Upload QR code image for customer payments (Optional)</li>
          <li>• <strong>Security:</strong> All banking information is encrypted and secure</li>
          <li>• <strong>Account Type:</strong> Use your business current account, not personal savings account</li>
          <li>• <strong>Verification:</strong> Double-check account number to avoid payment issues</li>
          <li>• <strong>Customer Payments:</strong> UPI QR code helps customers pay you easily</li>
          <li>• You can update or add multiple bank accounts later from settings</li>
        </ul>
      </CollapsibleInfo>
    </div>
  );
};

export default BankDetailsStep;
